// =============================================
// SRO Structure Panel (STOP-H z planu — UI dla A1 Pillar + C1 Author)
//
// Renderuje 2 sekcje przelaczane przez activeSection (mapowane na pipeline step keys):
// - "pillar" → PillarSection (lista pillar nodes + button "Utwórz Pillar Page" + button "Wygeneruj")
// - "author-page" → AuthorPageSection (button "Wygeneruj" per active writing profile)
//
// USUNIETE 2026-07-21: "cluster-coverage" (A2) + "entity-salience" (A4) — patrz
// DOCS/modules/SRO-PILLAR-AUTHOR-COVERAGE-SALIENCE.md. Coverage porownywal encje calej
// mapy do tresci samych filarow (19% zamiast realnych 61%) i liczyl szum z SERP-ow jako
// braki; Salience dawal tylko 3 nazwy w metryczce Schema.org, dzialajace dopiero po
// ponownej publikacji filaru. Oba byly advisory i niczego nie blokowaly.
//
// Gated per-mapa flaga pillar_pages_enabled + env-time flag (VITE_*_ENABLED) w service layer.
// =============================================

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Loader2, Plus, Sparkles, RefreshCw, FileText, Users, Trash2, Eye } from "lucide-react";
import type { ThematicMap, ThematicSeed, ThematicWritingProfile, ThematicNode } from "@/types/thematicMaps";
import { generateAuthorPage, type AuthorPageDebug } from "@/services/contentEngine/authorPageGenerator";
import { TabHelperBox } from "./TabHelperBox";
import { AuthorPagePreviewEditorModal } from "./AuthorPagePreviewEditorModal";
import type { SchemaPayload } from "@/services/schema/types";
import { uiLog } from "@/services/contentEngine/contentLogger";
import { hashQuestion } from "@/services/deduplicationService";
import { isPillarFlowV2Enabled } from "@/services/contentEngine/pillarGenerator";
import { PillarGenerationModal, type PillarGenerationMode } from "./PillarGenerationModal";
import { PillarPreviewModal } from "./PillarPreviewModal";
import { PillarBulkGenerationModal } from "./PillarBulkGenerationModal";
import { Layers } from "lucide-react";

interface SroStructurePanelProps {
  map: ThematicMap;
  seeds: ThematicSeed[];
  nodes: ThematicNode[];
  activeSection: "pillar" | "author-page";
  activeProfile: ThematicWritingProfile | null;
}

export function SroStructurePanel({
  map, seeds, nodes, activeSection, activeProfile,
}: SroStructurePanelProps) {
  return (
    <div className="space-y-6">
      {activeSection === "pillar" && (
        <PillarSection map={map} seeds={seeds} nodes={nodes} />
      )}
      {activeSection === "author-page" && (
        <AuthorPageSection map={map} activeProfile={activeProfile} />
      )}
    </div>
  );
}

// =============================================
// Section: Pillar Page
// =============================================

function PillarSection({ map, seeds, nodes }: { map: ThematicMap; seeds: ThematicSeed[]; nodes: ThematicNode[] }) {
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [genModal, setGenModal] = useState<{ open: boolean; nodeId: string; title: string; mode: PillarGenerationMode }>({
    open: false, nodeId: "", title: "", mode: "full",
  });
  const [previewModal, setPreviewModal] = useState<{ open: boolean; nodeId: string; title: string }>({
    open: false, nodeId: "", title: "",
  });
  const [nodeContents, setNodeContents] = useState<Map<string, { engine_stage: string | null; updated_at: string | null }>>(new Map());
  const pillarNodes = nodes.filter((n: any) => n.document_role === "pillar" || n.node_role === "pillar");
  const flagEnabled = Boolean(map.pillar_pages_enabled);
  const v2FlowEnabled = isPillarFlowV2Enabled();

  // Fetch content (engine_stage + updated_at) per pillar node — odswiezane na zmianie listy pillar nodes
  useEffect(() => {
    if (pillarNodes.length === 0) {
      setNodeContents(new Map());
      return;
    }
    let cancelled = false;
    const load = async () => {
      const ids = pillarNodes.map((n) => n.id);
      const { data } = await (supabase as any)
        .from("thematic_node_content")
        .select("node_id, engine_stage, updated_at")
        .in("node_id", ids);
      if (cancelled || !data) return;
      const m = new Map<string, { engine_stage: string | null; updated_at: string | null }>();
      for (const row of data) {
        m.set(row.node_id, { engine_stage: row.engine_stage, updated_at: row.updated_at });
      }
      setNodeContents(m);
    };
    load();
    // Re-load po close generation modal (bo engine_stage moglo sie zmienic)
    return () => { cancelled = true; };
  }, [pillarNodes.length, genModal.open]);

  const handleDelete = useCallback(async (nodeId: string, title: string) => {
    const ok = window.confirm(
      `Usunąć pillar page "${title}"?\n\nTo usunie:\n• pillar node + jego treść (final_html, debug)\n• relacje hub_to_spoke do spoków seeda\n\nNIE wpływa na:\n• spoki seeda (zostają nietknięte)\n• wpisy WP (jeśli pillar był publikowany — zostaje na WP, możesz usunąć ręcznie)\n• seed sam w sobie\n\nMożna potem utworzyć pillar od zera dla tego seeda.`,
    );
    if (!ok) return;
    setDeletingId(nodeId);
    try {
      const { error } = await (supabase as any).from("thematic_nodes").delete().eq("id", nodeId);
      if (error) throw new Error(error.message);
      toast({ title: "Pillar node usuniety", description: `"${title}" + content forensics removed. Refresh aby zobaczyc.` });
    } catch (err) {
      toast({ title: "Blad usuwania", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleGenerate = useCallback((nodeId: string, title: string, isRegenerate: boolean) => {
    if (!v2FlowEnabled) {
      toast({
        title: "VITE_PILLAR_FLOW_V2_ENABLED=false",
        description: "Wlacz flage w envie produkcyjnym (Coolify) zeby uzyc nowego pillarGenerator.",
        variant: "destructive",
      });
      return;
    }
    if (isRegenerate) {
      const ok = window.confirm(`Re-generuj pillar "${title}"?\n\nNadpisze obecny final_html + relations. Idempotent (PRE-STEP hub_to_spoke DELETE+INSERT, post-check dedupe).`);
      if (!ok) return;
    }
    setGenModal({ open: true, nodeId, title, mode: "full" });
  }, [v2FlowEnabled]);

  const handleRefreshLinks = useCallback((nodeId: string, title: string) => {
    if (!v2FlowEnabled) {
      toast({
        title: "VITE_PILLAR_FLOW_V2_ENABLED=false",
        description: "Wlacz flage w envie produkcyjnym zeby uzyc refreshPillarLinks.",
        variant: "destructive",
      });
      return;
    }
    setGenModal({ open: true, nodeId, title, mode: "refresh_links" });
  }, [v2FlowEnabled]);

  const handlePreview = useCallback((nodeId: string, title: string) => {
    setPreviewModal({ open: true, nodeId, title });
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Pillar Page (A1 SRO)</h3>
          <p className="text-sm text-muted-foreground">
            Hub klastra per seed. LLM wstawia linki w prozie (klon spokowego mechanizmu), potem deterministyczny post-check (dedupe + footer + hub_to_spoke relations). Re-runnable button "Odswiez linki" idempotent.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={flagEnabled ? "default" : "outline"} className={flagEnabled ? "bg-violet-500/15 text-violet-700" : ""}>
            {flagEnabled ? "Mapa: pillar enabled" : "Mapa: pillar disabled (UPDATE thematic_maps.pillar_pages_enabled=true)"}
          </Badge>
          <Badge variant={v2FlowEnabled ? "default" : "outline"} className={v2FlowEnabled ? "bg-emerald-500/15 text-emerald-700" : ""}>
            {v2FlowEnabled ? "V2 ON" : "V2 OFF (env)"}
          </Badge>
          <Button
            variant="outline"
            onClick={() => setShowBulkModal(true)}
            disabled={seeds.length === 0 || !v2FlowEnabled}
            title={v2FlowEnabled ? "Bulk: wygeneruj pillary dla wielu seedów sekwencyjnie" : "VITE_PILLAR_FLOW_V2_ENABLED=false (włącz w envie)"}
          >
            <Layers className="mr-1.5 h-4 w-4" /> Wygeneruj pillary
          </Button>
          <Button onClick={() => setShowModal(true)} disabled={seeds.length === 0}>
            <Plus className="mr-1.5 h-4 w-4" /> Utwórz Pillar Page
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <div className="px-4 py-3 border-b text-xs uppercase tracking-wide font-semibold text-muted-foreground">
          Pillar nodes w mapie ({pillarNodes.length})
        </div>
        <div className="p-4">
          {pillarNodes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak pillar nodów. Kliknij "Utwórz Pillar Page" zeby dodac (wymaga przynajmniej 1 seedu — masz {seeds.length}).</p>
          ) : (
            <ul className="space-y-3">
              {pillarNodes.map((node: any) => {
                const seed = seeds.find((s) => s.id === node.seed_id);
                const content = nodeContents.get(node.id);
                const engineStage = content?.engine_stage ?? "not_started";
                const isReady = engineStage === "ready" || engineStage === "published";
                const isGenerating = engineStage === "generating";

                return (
                  <li key={node.id} className="rounded-md border bg-muted/20 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{node.title}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          seed: {seed?.name ?? "(unknown)"} · slug: {node.slug}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          engine_stage: <span className="font-mono">{engineStage}</span>
                          {content?.updated_at && (
                            <> · last_gen: {new Date(content.updated_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}</>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant="outline" className="text-[10px]">{node.document_role}</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          title="Usuń pillar page (DELETE + cascade content/relations)"
                          onClick={() => handleDelete(node.id, node.title)}
                          disabled={deletingId === node.id}
                        >
                          {deletingId === node.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <Button
                        size="sm"
                        variant={isReady ? "outline" : "default"}
                        onClick={() => handleGenerate(node.id, node.title, isReady)}
                        disabled={isGenerating}
                        title={isReady ? "Re-generuj pillar (nadpisze final_html)" : "Generuj pillar (pierwsza generacja)"}
                      >
                        <Sparkles className="mr-1 h-3.5 w-3.5" />
                        {isReady ? "Re-generuj" : "Generuj"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRefreshLinks(node.id, node.title)}
                        disabled={!isReady}
                        title="Odswiez linki (idempotent: relations + dedupe + footer, BEZ regeneracji LLM)"
                      >
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                        Odswiez linki
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePreview(node.id, node.title)}
                        disabled={!isReady}
                        title="Podglad final_html + lista linkow w prozie + footer orphans"
                      >
                        <FileText className="mr-1 h-3.5 w-3.5" />
                        Podglad
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {showModal && (
        <PillarCreatorModal
          map={map}
          seeds={seeds}
          onClose={() => setShowModal(false)}
          onCreated={() => setShowModal(false)}
        />
      )}

      <PillarGenerationModal
        open={genModal.open}
        pillarNodeId={genModal.nodeId}
        pillarTitle={genModal.title}
        mapId={map.id}
        mode={genModal.mode}
        onClose={() => setGenModal((s) => ({ ...s, open: false }))}
      />

      <PillarPreviewModal
        open={previewModal.open}
        pillarNodeId={previewModal.nodeId}
        pillarTitle={previewModal.title}
        onClose={() => setPreviewModal((s) => ({ ...s, open: false }))}
      />

      <PillarBulkGenerationModal
        open={showBulkModal}
        map={map}
        seeds={seeds}
        nodes={nodes}
        onClose={() => setShowBulkModal(false)}
      />
    </div>
  );
}

function PillarCreatorModal({
  map, seeds, onClose, onCreated,
}: { map: ThematicMap; seeds: ThematicSeed[]; onClose: () => void; onCreated: () => void }) {
  // PER-SEED model (KOREKTA 2026-06-01): pillar = hub silosa JEDNEGO seeda.
  // Defaulty wyliczane z wybranego seeda. Title BEZ sufiksu "— przewodnik" (AI-tell, Cowork advisory):
  // user wpisuje naturalny tytul albo zostawia default = sama nazwa seeda.
  const [selectedSeedId, setSelectedSeedId] = useState<string>(seeds[0]?.id || "");
  const selectedSeed = seeds.find((s) => s.id === selectedSeedId) || null;
  const seedTopic = (selectedSeed?.name || "ten temat").trim();
  const defaultTitle = seedTopic;
  const defaultSlug = slugify(selectedSeed?.slug || selectedSeed?.name || "pillar");
  const defaultCanonical = `Czym jest ${seedTopic} i od czego zacząć?`;
  const [title, setTitle] = useState<string>(defaultTitle);
  const [slug, setSlug] = useState<string>(defaultSlug);
  const [canonicalQuestion, setCanonicalQuestion] = useState<string>(defaultCanonical);
  const [submitting, setSubmitting] = useState(false);

  // Reset defaultow gdy zmieni sie wybrany seed (UX convention "select-driven defaults")
  useEffect(() => {
    if (!selectedSeed) return;
    const topic = (selectedSeed.name || "ten temat").trim();
    setTitle(topic);
    setSlug(slugify(selectedSeed.slug || selectedSeed.name || "pillar"));
    setCanonicalQuestion(`Czym jest ${topic} i od czego zacząć?`);
  }, [selectedSeedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(async () => {
    if (!selectedSeedId || !title.trim() || !slug.trim()) {
      toast({ title: "Brak danych", description: "Wybierz seed + wprowadź title + slug", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // INSERT pillar node (1 per seed via idx_unique_pillar_per_seed partial UNIQUE)
      const { data: { user } } = await (supabase as any).auth.getUser();
      if (!user) throw new Error("Nie zalogowany");

      const canonicalQ = canonicalQuestion.trim() || title.trim();
      const { data: newNode, error: insErr } = await (supabase as any)
        .from("thematic_nodes")
        .insert({
          user_id: user.id,
          map_id: map.id,
          seed_id: selectedSeedId,
          title: title.trim(),
          slug: slug.trim(),
          canonical_question: canonicalQ,
          canonical_question_hash: hashQuestion(canonicalQ),
          document_role: "pillar",
          node_role: "pillar",
          node_status: "planned",
          map_version: map.map_version,
          sort_order: 0,
        })
        .select("id")
        .single();

      if (insErr) throw new Error(`INSERT node: ${insErr.message}`);

      toast({
        title: "Pillar node utworzony",
        description: `Node "${title.trim()}" gotowy. Otwórz Content Engine i kliknij Generate (gated VITE_PILLAR_FLOW_ENABLED).`,
      });
      uiLog.public("Pillar", `Created pillar node ${newNode.id} w mapie ${map.id}`);
      onCreated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: "Blad", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }, [map, selectedSeedId, title, slug, canonicalQuestion, onCreated]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Utwórz Pillar Page</DialogTitle>
          <DialogDescription>
            Pillar = hub silosa JEDNEGO seeda. Max 1 pillar per seed. Pokrywa i linkuje spoki TEGO seeda.
            Po utworzeniu otwórz Content Engine i kliknij Generate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Seed (silo pillara)</Label>
            <Select value={selectedSeedId} onValueChange={setSelectedSeedId}>
              <SelectTrigger>
                <SelectValue placeholder="Wybierz seed..." />
              </SelectTrigger>
              <SelectContent>
                {seeds.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} (priority: {s.priority})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Defaulty tytułu/slug/pytania wyliczane z wybranego seeda — pola edytowalne.
            </p>
          </div>
          <div>
            <Label>Title (H1)</Label>
            <Input value={title} onChange={(e) => { setTitle(e.target.value); setSlug(slugify(e.target.value)); }} />
            <p className="mt-1 text-xs text-muted-foreground">
              Sugestia: rozbuduj naturalnie z konkretem — np. „Eksperymenty wzrostowe: jak testować i skalować to, co działa". Unikaj sufiksów „— przewodnik" / „— kompletny poradnik" (AI-tell).
            </p>
          </div>
          <div>
            <Label>Slug (URL)</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} className="font-mono text-sm" />
          </div>
          <div>
            <Label>Canonical question</Label>
            <Textarea
              value={canonicalQuestion}
              onChange={(e) => setCanonicalQuestion(e.target.value)}
              rows={3}
              placeholder="Realne pytanie o temat seeda (default: 'Czym jest {seed} i od czego zacząć?')"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anuluj</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedSeedId || !title.trim() || !slug.trim()}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Utwórz pillar node
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// Section: Author Page (C1)
// =============================================

function AuthorPageSection({ map, activeProfile }: { map: ThematicMap; activeProfile: ThematicWritingProfile | null }) {
  const [generating, setGenerating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitial, setModalInitial] = useState<{
    html: string | null;
    savedHtml: string | null;
    debug: AuthorPageDebug | null;
    schema: SchemaPayload | null;
  }>({ html: null, savedHtml: null, debug: null, schema: null });

  const handleReset = useCallback(async () => {
    if (!activeProfile) {
      toast({ title: "Brak profilu", description: "Nic do resetowania.", variant: "destructive" });
      return;
    }
    const ok = window.confirm(
      `[DEBUG] Reset author page status dla profilu "${activeProfile.public_author_label || activeProfile.id}"?\n\nUPDATE thematic_writing_profiles SET\n  author_page_wp_post_id=NULL,\n  author_page_generated_at=NULL,\n  author_page_html=NULL\nWHERE id='${activeProfile.id}'.\n\nUsuwa zapisany HTML + status. NIE odlacza is_enabled.`,
    );
    if (!ok) return;
    setResetting(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_writing_profiles")
        .update({ author_page_wp_post_id: null, author_page_generated_at: null, author_page_html: null })
        .eq("id", activeProfile.id);
      if (error) throw new Error(error.message);
      toast({ title: "Author page status zresetowany", description: "wp_post_id + generated_at + html = NULL." });
    } catch (err) {
      toast({ title: "Blad reset", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setResetting(false);
    }
  }, [activeProfile]);

  const handleGenerate = useCallback(async () => {
    if (!activeProfile) {
      toast({ title: "Brak profilu", description: "Najpierw aktywuj writing profile (zakladka Profil).", variant: "destructive" });
      return;
    }
    setGenerating(true);
    try {
      const res = await generateAuthorPage(activeProfile.id, map.id);
      if (res.success && res.html) {
        setModalInitial({
          html: res.html,
          savedHtml: activeProfile.author_page_html ?? null,
          debug: res.debug || null,
          schema: res.schema || null,
        });
        setModalOpen(true);
        toast({
          title: "Strona autora wygenerowana",
          description: `${res.html.length} znakow • $${res.costUsd.toFixed(4)}${res.schema ? " • ProfilePage schema" : ""} • kliknij Zapisz w modalu aby utrwalic.`,
        });
      } else {
        toast({ title: "Blad", description: res.error || "Nieznany", variant: "destructive" });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: "Blad", description: msg, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  }, [map.id, activeProfile]);

  const handleOpenSavedPreview = useCallback(() => {
    if (!activeProfile?.author_page_html) return;
    setModalInitial({
      html: activeProfile.author_page_html,
      savedHtml: activeProfile.author_page_html,
      debug: null,
      schema: null,
    });
    setModalOpen(true);
  }, [activeProfile]);

  const hasProfile = Boolean(activeProfile);
  const hasBio = Boolean(activeProfile?.author_bio_html);
  const flagEnabled = Boolean(activeProfile?.author_page_enabled);
  const lastGen = activeProfile?.author_page_generated_at;
  const savedHtml = activeProfile?.author_page_html ?? null;
  const hasSavedHtml = Boolean(savedHtml);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Strona autora (C1 SRO)</h3>
          <p className="text-sm text-muted-foreground">
            Dedykowana strona "O autorze" z ProfilePage + standalone Person schema. Pomija SERP/KG/Fan-Out — tylko profile context + LLM.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={flagEnabled ? "default" : "outline"} className={flagEnabled ? "bg-pink-500/15 text-pink-700" : ""}>
            {flagEnabled ? "Profile: enabled" : "Profile: disabled"}
          </Badge>
          <Button onClick={handleGenerate} disabled={generating || !hasProfile || !hasBio}>
            {generating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}
            {hasSavedHtml ? "Re-generuj" : "Wygeneruj Stronę Autora"}
          </Button>
          <Button
            variant="outline"
            onClick={handleOpenSavedPreview}
            disabled={!hasSavedHtml}
            title={hasSavedHtml ? "Otwórz modal z zapisanym HTML (Podgląd / HTML / Info)" : "Brak zapisanego HTML — najpierw wygeneruj"}
            className={hasSavedHtml ? "border-pink-500/40 text-pink-700 dark:text-pink-300 hover:bg-pink-500/10" : ""}
          >
            <Eye className="mr-1.5 h-4 w-4" />
            Podgląd / Edytuj
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10"
            title="[DEBUG] Reset author_page_wp_post_id + generated_at + html = NULL"
            onClick={handleReset}
            disabled={resetting || !hasProfile}
          >
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card p-4 space-y-2 text-sm">
        {!hasProfile ? (
          <p className="text-muted-foreground">Brak aktywnego writing profile. Otwórz zakladke "Profil" i wygeneruj.</p>
        ) : !hasBio ? (
          <p className="text-amber-600">Profile {activeProfile?.public_author_label} bez author_bio_html. Najpierw uzupelnij bio.</p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{activeProfile?.public_author_label}</span>
              {activeProfile?.author_url && (
                <a href={activeProfile.author_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline truncate">
                  {activeProfile.author_url}
                </a>
              )}
            </div>
            {lastGen && (
              <p className="text-xs text-muted-foreground">Ostatnio wygenerowano: {new Date(lastGen).toLocaleString("pl")}</p>
            )}
            <div className="flex items-center gap-2 pt-1">
              {hasSavedHtml ? (
                <Badge variant="default" className="bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/40">
                  Zapisano · {savedHtml!.length} chars
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">Brak zapisanego HTML</Badge>
              )}
            </div>
          </>
        )}
      </div>

      {modalOpen && activeProfile && (
        <AuthorPagePreviewEditorModal
          open={modalOpen}
          profileId={activeProfile.id}
          profileLabel={activeProfile.public_author_label || activeProfile.id.slice(0, 8)}
          authorUrl={activeProfile.author_url}
          lastGeneratedAt={activeProfile.author_page_generated_at}
          mapId={map.id}
          initialHtml={modalInitial.html}
          initialSavedHtml={modalInitial.savedHtml}
          initialDebug={modalInitial.debug}
          initialSchema={modalInitial.schema}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

// =============================================
// Helpers
// =============================================

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}
